import { type ReactNode, useState } from 'react';
import type { FieldMetadata, ObjectMetadata, RecordRow } from '../../data/client';
import { fieldShape, isSelectableField } from '../../fields/fieldTypeMeta';
import { IDENTITY_HIGHLIGHT_LIMIT, IdentityBand, type IdentityHighlight } from '../../ui';
import { isEmptyValue } from './fieldGroups';
import { ReadOnlyFieldValue } from './ReadOnlyFieldValue';
import { RecordFieldEditor } from './RecordFieldEditor';
import { useRecordFieldSave } from './useRecordFieldSave';

/**
 * WHICH VALUES THE BAND SHOWS: THE OBJECT'S KEY FACTS, FILLED ONES ONLY (BR-RECORD; handbook
 * DESIGN.md G2 and H2, register A6-13).
 *
 * G2: "key facts fixed per object, not schema order". The rule this replaces took the first six
 * fields by `position`, which on an agency was Instagram handle, Website, Country, Language,
 * Shipping address and Telegram group - empty on every live record and repeated in the rows
 * under the band (the owner on 4 Oct: "Man sieht ganz oben als allererstes Instagram Handle,
 * Website und Country, was bei jedem leer ist"). The list is CONFIGURATION now: each key fact
 * carries `settings.keyFact`, its place in the band, which the OFMOS seed writes per object
 * (`seed/model.ts` `keyFacts`). This file reads the number and names no object.
 *
 * H2: "Show a ... header fact only when most live records fill it". The configuration answers
 * "most"; THIS record answers "filled": a key fact this record has no value for is not drawn
 * as a dash, it is left out, and its row waits under the band in the folded empty fields.
 */
export function keyFactFields(fields: readonly FieldMetadata[]): readonly FieldMetadata[] {
  return fields
    .filter(
      (field) =>
        field.options?.keyFact !== undefined &&
        isSelectableField(field) &&
        /**
         * A HIGHLIGHT IS A VALUE, AND AN ATTACHMENT LIST IS NOT ONE. A files field has
         * no closed display: it renders the live list with a Download and a Remove per
         * file (`file-attachments.md` D18), so putting it in a header would put a second
         * copy of those controls on the screen - which is what a browser test found as
         * "getByRole('list', { name: 'Papers files' }) resolved to 2 elements".
         */
        fieldShape(field.type) !== 'files',
    )
    .toSorted(
      (a, b) => (a.options?.keyFact ?? 0) - (b.options?.keyFact ?? 0) || a.position - b.position,
    )
    .slice(0, IDENTITY_HIGHLIGHT_LIMIT);
}

/** The key facts THIS record has filled, in band order: what the band draws. */
export function shownKeyFacts(
  fields: readonly FieldMetadata[],
  record: RecordRow,
): readonly FieldMetadata[] {
  return keyFactFields(fields).filter((field) => !isEmptyValue(record.values[field.id]));
}

/**
 * THE SAME FIELDS, AS IDS, for the rows under the band: a value the band shows is not said a
 * second time below it (A6-13, "with the same fields repeated below"). One function picks the
 * fields, so the band and the rows can never disagree about which values the band holds.
 */
export function highlightFieldIds(
  fields: readonly FieldMetadata[],
  record: RecordRow,
): readonly string[] {
  return shownKeyFacts(fields, record).map((field) => field.id);
}

/**
 * The band's values, read-only: the phone's grouped list and any surface that has no editor
 * of its own (the task detail pane). The record page and the list's pane use `KeyFactBand`,
 * which draws the same values as controls.
 */
export function identityHighlights(
  object: ObjectMetadata,
  fields: readonly FieldMetadata[],
  record: RecordRow,
): readonly IdentityHighlight[] {
  return shownKeyFacts(fields, record).map((field) => ({
    label: field.label,
    value: (
      <ReadOnlyFieldValue
        objectId={object.id}
        recordId={record.id}
        field={field}
        value={record.values[field.id] ?? null}
        humanOwned={record.provenance[field.id] !== undefined}
      />
    ),
  }));
}

/**
 * THE BAND'S VALUES AS CONTROLS, AND THE ONE EDITOR THEY OPEN.
 *
 * Taking a value out of the rows under the band takes its editor with it, and a key fact a
 * coach cannot correct would be worse than one said twice. So each value in the band is the
 * row's own control - the value stays in the reader's eye, a full-size layer behind it opens
 * the ordinary `RecordFieldEditor` (G13: "edits in place") directly under the band, and links
 * inside the value stay clickable, exactly as `RecordFieldRow` builds it. An archived record
 * and a computed field draw the value with no control, as the rows do (D21, D2).
 */
export function KeyFactBand({
  object,
  fields,
  record,
  name,
  tile,
}: {
  readonly object: ObjectMetadata;
  readonly fields: readonly FieldMetadata[];
  readonly record: RecordRow;
  readonly name: string;
  readonly tile: ReactNode;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const { save } = useRecordFieldSave(object.id, record.id);
  const readOnly = record.deletedAt !== null;
  const shown = shownKeyFacts(fields, record);

  const highlights = shown.map((field) => {
    const value = record.values[field.id] ?? null;
    const humanOwned = record.provenance[field.id] !== undefined;
    const locked = readOnly || field.options?.computed === true;
    return {
      label: field.label,
      value: (
        <span className="pointer-events-none relative block [&_a]:pointer-events-auto [&_button]:pointer-events-auto">
          {!locked && (
            <button
              type="button"
              /* `hit-expand-in-place`: the value is one line, 20px, and the layer's own
                 pseudo-element brings the hit area to the touch floor (DESIGN.md K, measure R5). */
              className="hit-expand-in-place pointer-events-auto absolute inset-0 rounded-control"
              aria-expanded={editingId === field.id}
              onClick={() => setEditingId(editingId === field.id ? null : field.id)}
            >
              <span className="sr-only">Edit {field.label}</span>
            </button>
          )}
          {/* POSITIONED, so the value paints above the edit layer and a link in it (a CSM, a
              document) is the link and not the layer under it; the rest of the value lets the
              pointer through to the layer. */}
          <span className="relative">
            <ReadOnlyFieldValue
              objectId={object.id}
              recordId={record.id}
              field={field}
              value={value}
              humanOwned={humanOwned}
            />
          </span>
        </span>
      ),
    };
  });

  const editing = shown.find((field) => field.id === editingId);
  const editor =
    editing === undefined || readOnly ? null : (
      <RecordFieldEditor
        key={editing.id}
        objectId={object.id}
        recordId={record.id}
        field={editing}
        value={record.values[editing.id] ?? null}
        humanOwned={record.provenance[editing.id] !== undefined}
        onCancel={() => setEditingId(null)}
        onCommit={async (next) => {
          setEditingId(null);
          await save(editing, next);
        }}
      />
    );

  return (
    <>
      <IdentityBand
        name={name}
        secondary={object.labelSingular}
        tile={tile}
        highlights={highlights}
      />
      {editor}
    </>
  );
}

/**
 * THE SAME IDENTITY ON A PHONE, AS AN INSET GROUPED LIST (P5-3,
 * `spec/screens/record.md` section 3 item 3): a tile row carrying the name and the
 * object, then the filled key facts as label-and-value rows.
 *
 * It is a different COMPONENT and not the desktop band under a media query, because
 * the two are different Apple parts: the band is a Mail-style header on the content
 * surface, and this is the kit's Grouped Table View - a white card with a 26 radius
 * on the grouped background, which is what a phone screen of values looks like on iOS.
 */
export function RecordIdentityList({
  name,
  secondary,
  tile,
  highlights,
}: {
  readonly name: string;
  readonly secondary: ReactNode;
  readonly tile: ReactNode;
  readonly highlights: readonly IdentityHighlight[];
}) {
  return (
    <div className="record-grouped">
      <div className="record-grouped-row">
        {tile}
        <div className="min-w-0 flex-1">
          <p className="record-grouped-label">{name}</p>
          <p className="record-band-secondary">{secondary}</p>
        </div>
      </div>
      {highlights.slice(0, IDENTITY_HIGHLIGHT_LIMIT).map((highlight) => (
        <div key={highlight.label} className="record-grouped-row">
          <span className="record-grouped-label">{highlight.label}</span>
          <span className="record-grouped-value">{highlight.value ?? '\u2014'}</span>
        </div>
      ))}
    </div>
  );
}
